import {
  ComposedChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export interface ChartDataPoint {
  name: string;
  recebido: number;
  vencido: number;
  aReceber: number;
  projecao: number;
}

interface CashFlowChartProps {
  data: ChartDataPoint[];
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
  }).format(value);
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
  }>;
  label?: string;
}

const labelMap: Record<string, string> = {
  recebido: 'Recebido',
  vencido: 'Vencido',
  aReceber: 'A Receber',
  projecao: 'Projeção IA',
};

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-lg">
      <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center justify-between gap-4 text-sm">
          <span className="flex items-center gap-2">
            <span 
              className="w-2 h-2 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">
              {labelMap[entry.dataKey] || entry.dataKey}
            </span>
          </span>
          <span className="font-semibold text-foreground">
            {formatCurrency(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function CashFlowChart({ data }: CashFlowChartProps) {
  // Calculate max value for dynamic Y axis
  const maxValue = Math.max(
    ...data.map(d => Math.max(d.recebido, d.vencido, d.aReceber, d.projecao))
  );
  const yAxisMax = Math.ceil(maxValue / 10000) * 10000 || 10000;

  return (
    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="projecaoGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(270, 70%, 60%)" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="hsl(270, 70%, 60%)" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis 
            dataKey="name" 
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
            tickLine={false}
          />
          <YAxis 
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            domain={[0, yAxisMax]}
            tickFormatter={(value) => value >= 1000 ? `${value / 1000}k` : value}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="bottom"
            height={36}
            formatter={(value) => (
              <span className="text-xs text-muted-foreground">
                {labelMap[value] || value}
              </span>
            )}
          />
          <Area
            type="monotone"
            dataKey="projecao"
            stroke="hsl(270, 70%, 60%)"
            strokeWidth={2}
            fill="url(#projecaoGradient)"
            strokeDasharray="5 5"
            animationDuration={800}
          />
          <Bar 
            dataKey="recebido" 
            fill="hsl(var(--accent-500))" 
            radius={[4, 4, 0, 0]}
            animationDuration={800}
          />
          <Bar 
            dataKey="vencido" 
            fill="hsl(var(--careful-500))" 
            radius={[4, 4, 0, 0]}
            animationDuration={800}
          />
          <Bar 
            dataKey="aReceber" 
            fill="hsl(var(--basic-500))" 
            radius={[4, 4, 0, 0]}
            animationDuration={800}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
